import { find, isPlainObject, first, isArray } from 'lodash'
import maybe from 'folktale/maybe'

import {
  shouldResolveCurrentUser,
  shouldResolveDataBinding,
  shouldResolveUserInput,
  shouldResolveSlug,
  serializeFilter,
  FILTER_CONDITIONS,
} from '@wix/wix-data-client-common'

import { FilterCondition } from '@wix/wix-data-client-common/dist/esm/filterCondition'
import { createFilterExpression, generateUniqueFilterId } from './filterUtils'

import {
  createUserInputFilterBinding,
  createDatasetFilterBinding,
  getFilterBindingFieldName,
  hasFilterBindingById,
} from '../connections/connectionConfigHelper'

import * as filterByDataset from './filterByDataset'
import * as filterByUserInput from '../filter/filterByUserInput'

const firstKey = obj => Object.keys(obj)[0]

const ensureFilterId = filter =>
  filter.filterId
    ? filter
    : {
        ...filter,
        filterId: generateUniqueFilterId(),
      }

const isFilterByDataset = filter => isPlainObject(filter.dataset)

const isFilterByUserInput = filter => isPlainObject(filter.component)

// (filter expression) -> { (dataset filter), (connection) }
const extractFilterAndConnection = (filter, datasetRef) => {
  if (isFilterByUserInput(filter)) {
    const finalFilter = ensureFilterId(filter)

    return {
      filters: [finalFilter],
      connections: [
        filterByUserInput.createUserInputConnection({
          datasetRef,
          componentRef: filter.component,
          connectionConfig: createUserInputFilterBinding(finalFilter.filterId),
        }),
      ],
    }
  }
  if (isFilterByDataset(filter)) {
    const finalFilter = ensureFilterId(filter)

    return {
      filters: [finalFilter],
      connections: [
        filterByDataset.createMasterDetailConnection({
          masterRef: filter.dataset,
          detailRef: datasetRef,
          connectionConfig: createDatasetFilterBinding(
            finalFilter.filterId,
            finalFilter.datasetField,
          ),
        }),
      ],
    }
  }
  return { filters: [filter], connections: [] }
}

const getEmptinessConditionType = condition =>
  condition === FilterCondition.EQUALS
    ? FilterCondition.IS_EMPTY
    : FilterCondition.IS_NOT_EMPTY

const deserializeField = serializedField => {
  const [field, path] = serializedField.split('.')
  return { field, path, fieldWithPath: serializedField }
}

const deserializeQuery = connections => wixDataFieldQuery => {
  const positiveQuery = firstKey(wixDataFieldQuery) !== '$not'
  const fieldQuery = positiveQuery
    ? wixDataFieldQuery
    : wixDataFieldQuery.$not[0]

  const { field, path, fieldWithPath } = deserializeField(firstKey(fieldQuery))

  const operator = firstKey(fieldQuery[fieldWithPath])
  const condition = find(
    FILTER_CONDITIONS,
    condition =>
      condition.operator === operator && condition.positive === positiveQuery,
  )
  const valueWrappedInArray = isArray(fieldQuery[fieldWithPath][operator])
  const possiblyDynamicFilterValue =
    condition.singleValue || !valueWrappedInArray
      ? fieldQuery[fieldWithPath][operator]
      : first(fieldQuery[fieldWithPath][operator])

  if (shouldResolveCurrentUser(possiblyDynamicFilterValue)) {
    return createFilterExpression({ field, condition: condition.type })
  }
  if (shouldResolveSlug(possiblyDynamicFilterValue)) {
    return createFilterExpression({
      field,
      condition: condition.type,
      slug: true,
    })
  }
  const connection = maybe.fromNullable(
    possiblyDynamicFilterValue &&
      connections.find(connection =>
        hasFilterBindingById(
          connection.config,
          possiblyDynamicFilterValue.filterId,
        ),
      ),
  )

  if (shouldResolveUserInput(possiblyDynamicFilterValue)) {
    return createFilterExpression({
      field,
      condition: condition.type,
      component: connection.map(con => con.componentRef).getOrElse({}),
      componentType: connection.map(con => con.componentType).getOrElse(null),
      filterId: possiblyDynamicFilterValue.filterId,
    })
  }

  if (shouldResolveDataBinding(possiblyDynamicFilterValue)) {
    const datasetField = connection
      .chain(({ config }) =>
        maybe.fromNullable(
          getFilterBindingFieldName(
            config,
            possiblyDynamicFilterValue.filterId,
          ),
        ),
      )
      .getOrElse('')

    const datasetRef = connection
      .map(con => filterByDataset.getMasterRefFromConnection(con))
      .getOrElse({})

    return createFilterExpression({
      field,
      condition: condition.type,
      dataset: datasetRef,
      datasetField,
      filterId: possiblyDynamicFilterValue.filterId,
      path,
      datasetFieldIsArray: !condition.singleValue && !valueWrappedInArray,
    })
  }

  const staticFilterValue = fieldQuery[fieldWithPath][operator]

  return createFilterExpression({
    field,
    condition:
      staticFilterValue === null
        ? getEmptinessConditionType(condition.type)
        : condition.type,
    value: staticFilterValue,
    path,
  })
}

const deserializeFilter = (wixDataFilter, connections) =>
  wixDataFilter ? wixDataFilter.$and.map(deserializeQuery(connections)) : []

export {
  serializeFilter,
  deserializeFilter,
  extractFilterAndConnection,
  isFilterByUserInput,
}
